import { WeibullUser } from '@/contexts/CognitoAuthContext';
import { Workspace, WorkspaceListItem, BackendWorkspace, BackendWorkspaceWithVersion } from '@/types/workspace';

// Minimal workspace interface for permission checks
export interface MinimalWorkspace {
  id: string;
  name: string;
  createdBy?: { id: string; name: string };
  owner?: string;
}

/**
 * Check if a user can edit a workspace based on RBAC rules
 * 
 * Rules:
 * - ONYX users can edit any workspace
 * - Customer Admin users can edit any workspace
 * - Regular users can only edit their own workspaces
 */
export const canUserEditWorkspace = (
  user: WeibullUser | null,
  workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace | null
): boolean => {
  if (!user || !workspace) return false;

  // ONYX users can edit any workspace
  if (user.company === 'onyx') {
    return true;
  }

  // Customer admins can edit any workspace  
  if (user.company === 'company' && user.role === 'admin') {
    return true;
  }

  // Check ownership for different workspace types
  let ownerId: string | undefined;
  
  // Safely check for workspace properties
  try {
    if (workspace && typeof workspace === 'object' && 'createdBy' in workspace && workspace.createdBy) {
      // Frontend Workspace or WorkspaceListItem type
      ownerId = workspace.createdBy.id;
    } else if (workspace && typeof workspace === 'object' && 'owner' in workspace) {
      // Backend workspace types
      ownerId = workspace.owner;
    }
  } catch (error) {
    // Handle edge cases where 'in' operator fails
    return false;
  }

  // Regular users can only edit their own workspaces
  return ownerId === user.id;
};

/**
 * Check if a user can delete a workspace based on RBAC rules
 * 
 * Rules:
 * - ONYX users can delete any workspace
 * - Customer Admin users can delete any workspace
 * - Regular users cannot delete workspaces
 */
export const canUserDeleteWorkspace = (
  user: WeibullUser | null,
  workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace | null
): boolean => {
  if (!user || !workspace) return false;

  // ONYX users can delete any workspace
  if (user.company === 'onyx') {
    return true;
  }

  // Customer admins can delete any workspace
  if (user.company === 'company' && user.role === 'admin') {
    return true;
  }

  // Regular users cannot delete workspaces
  return false;
};

/**
 * Check if a user can create a workspace
 * 
 * Rules:
 * - All authenticated users can create workspaces
 */
export const canUserCreateWorkspace = (user: WeibullUser | null): boolean => {
  return user !== null;
};

/**
 * Check if a user can view/load a workspace
 * 
 * Rules:
 * - All authenticated users can view/load any workspace
 */
export const canUserViewWorkspace = (
  user: WeibullUser | null,
  workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace | null
): boolean => {
  // All authenticated users can view any workspace
  return user !== null && workspace !== null;
};

/**
 * Check if the user is the owner of a workspace
 */
export const isWorkspaceOwner = (
  user: WeibullUser | null,
  workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace | null
): boolean => {
  if (!user || !workspace) return false;

  // Check ownership for different workspace types
  let ownerId: string | undefined;
  
  // Safely check for workspace properties
  try {
    if (workspace && typeof workspace === 'object' && 'createdBy' in workspace && workspace.createdBy) {
      // Frontend Workspace or WorkspaceListItem type
      ownerId = workspace.createdBy.id;
    } else if (workspace && typeof workspace === 'object' && 'owner' in workspace) {
      // Backend workspace types
      ownerId = workspace.owner;
    }
  } catch (error) {
    // Handle edge cases where 'in' operator fails
    return false;
  }

  return ownerId === user.id;
};

/**
 * Get permission summary for a workspace
 */
export const getWorkspacePermissions = (
  user: WeibullUser | null,
  workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace | null
) => {
  return {
    canView: canUserViewWorkspace(user, workspace),
    canEdit: canUserEditWorkspace(user, workspace),
    canDelete: canUserDeleteWorkspace(user, workspace),
    isOwner: isWorkspaceOwner(user, workspace),
  };
};

/**
 * Filter workspaces by user permissions
 */
export const filterWorkspacesByPermission = <T extends Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace>(
  user: WeibullUser | null,
  workspaces: T[],
  permission: 'view' | 'edit' | 'delete'
): T[] => {
  if (!user) return [];

  switch (permission) {
    case 'view':
      return workspaces.filter(w => canUserViewWorkspace(user, w));
    case 'edit':
      return workspaces.filter(w => canUserEditWorkspace(user, w));
    case 'delete':
      return workspaces.filter(w => canUserDeleteWorkspace(user, w));
    default:
      return workspaces;
  }
};